// Expenses (SPEC.md §4.3): one currency from settings, amounts as integer minor units.
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';

export type ExpenseCategory = 'session_fee' | 'medication' | 'lab' | 'transport' | 'consultation' | 'supplies' | 'other';

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  session_fee: 'Dialysis session',
  medication: 'Medicines',
  lab: 'Lab tests',
  transport: 'Transport',
  consultation: 'Doctor visit',
  supplies: 'Supplies',
  other: 'Other'
};

export interface Expense {
  id: string;
  expense_date: string;
  category: ExpenseCategory;
  description: string;
  amount_minor: number;
  session_id: string | null;
  notes: string | null;
}

/** Expenses dated in [from, to] ('yyyy-MM-dd'), newest first. */
export function useExpenses(from: string, to: string) {
  const query = useDbQuery<Expense>(
    'SELECT id, expense_date, category, description, amount_minor, session_id, notes FROM expenses WHERE deleted_at IS NULL AND expense_date >= ? AND expense_date <= ? ORDER BY expense_date DESC, created_at DESC',
    [from, to],
    ['expenses']
  );
  return { expenses: query.data ?? [], isLoading: query.isLoading };
}

export function saveExpense(db: DbClient, id: string | undefined, values: Omit<Expense, 'id'>) {
  return id ? db.update('expenses', id, values) : db.insert('expenses', values);
}

export function deleteExpense(db: DbClient, id: string) {
  return db.softDelete('expenses', id);
}

/** Total per category, largest first. */
export function totalsByCategory(expenses: Pick<Expense, 'category' | 'amount_minor'>[]) {
  const totals = new Map<ExpenseCategory, number>();
  for (const expense of expenses) totals.set(expense.category, (totals.get(expense.category) ?? 0) + expense.amount_minor);
  return [...totals.entries()].map(([category, total]) => ({ category, total })).sort((a, b) => b.total - a.total);
}
